import React, { useRef, useState, useEffect } from "react";
import { Card, Table, Button, Modal, Form, Input, message, Popconfirm, Tag, Switch, Space } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { PageLoadingOverlay } from "../components/RoomLoadingScene";

const subAccountCache = new Map();

const SubAccountScreen = () => {
    const userId = JSON.parse(localStorage.getItem("user") || "{}").id || "anonymous";
    const cachedSubAccounts = subAccountCache.get(userId);
    const [subAccounts, setSubAccounts] = useState(cachedSubAccounts || []);
    const [loading, setLoading] = useState(!cachedSubAccounts);
    const [modalVisible, setModalVisible] = useState(false);
    const [creating, setCreating] = useState(false);
    const creatingRef = useRef(false);
    const [form] = Form.useForm();

    useEffect(() => { loadSubAccounts(Boolean(cachedSubAccounts)); }, []);

    const loadSubAccounts = async (quiet = false) => {
        try {
            if (!quiet) setLoading(true);
            const token = localStorage.getItem("token");
            const res = await fetch("/api/sub-accounts", {
                headers: { Authorization: "Bearer " + token }
            });
            const data = await res.json();
            setSubAccounts(data);
            subAccountCache.set(userId, data);
        } catch (e) {
            message.error("加载失败");
        } finally {
            if (!quiet) setLoading(false);
        }
    };

    const handleCreate = async () => {
        if (creatingRef.current) return;
        creatingRef.current = true;
        setCreating(true);
        try {
            const values = await form.validateFields();
            const token = localStorage.getItem("token");
            const res = await fetch("/api/sub-accounts", {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
                body: JSON.stringify(values)
            });
            const data = await res.json();
            if (res.ok) {
                message.success("子账号创建成功，默认密码：" + values.password);
                setModalVisible(false);
                form.resetFields();
                loadSubAccounts(true);
            } else {
                message.error(data.error || "创建失败");
            }
        } catch (e) {
            if (!e?.errorFields) message.error("创建失败");
        } finally {
            creatingRef.current = false;
            setCreating(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            const token = localStorage.getItem("token");
            const res = await fetch("/api/sub-accounts/" + id, {
                method: "DELETE",
                headers: { Authorization: "Bearer " + token }
            });
            if (res.ok) {
                message.success("删除成功");
                loadSubAccounts(true);
            } else {
                const data = await res.json();
                message.error(data.error || "删除失败");
            }
        } catch (e) {
            message.error("删除失败");
        }
    };

    const handlePermissionChange = async (subAccountId, permissionKey, value) => {
        try {
            const token = localStorage.getItem("token");
            const subAccount = subAccounts.find(s => s.id === subAccountId);
            const newPermissions = { ...subAccount.permissions, [permissionKey]: value };
            
            const res = await fetch("/api/sub-accounts/" + subAccountId + "/permissions", {
                method: "PUT",
                headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
                body: JSON.stringify(newPermissions)
            });
            if (res.ok) {
                message.success("权限更新成功");
                loadSubAccounts(true);
            } else {
                const data = await res.json();
                message.error(data.error || "更新失败");
            }
        } catch (e) {
            message.error("更新失败");
        }
    };

    const columns = [
        { title: "序号", key: "index", width: 60, render: (_, __, index) => index + 1 },
        { title: "用户名", dataIndex: "username", key: "username" },
        { title: "手机号", dataIndex: "phone", key: "phone" },
        { 
            title: "房源管理权限", 
            key: "manage_permission", 
            width: 120,
            render: (_, record) => (
                <Switch 
                    checked={record.permissions?.can_manage_rooms !== false} 
                    onChange={(checked) => handlePermissionChange(record.id, "can_manage_rooms", checked)}
                />
            )
        },
        { 
            title: "发布上架权限", 
            key: "publish_permission", 
            width: 120,
            render: (_, record) => (
                <Switch 
                    checked={record.permissions?.can_publish !== false} 
                    onChange={(checked) => handlePermissionChange(record.id, "can_publish", checked)}
                />
            )
        },
        {
            title: "报修工单权限",
            key: "repair_work_order_permission",
            width: 130,
            render: (_, record) => (
                <Switch
                    checked={record.permissions?.can_manage_repair_work_orders === true}
                    onChange={(checked) => handlePermissionChange(record.id, "can_manage_repair_work_orders", checked)}
                />
            )
        },
        {
            title: "保洁工单权限",
            key: "cleaning_work_order_permission",
            width: 130,
            render: (_, record) => (
                <Switch
                    checked={record.permissions?.can_manage_cleaning_work_orders === true}
                    onChange={(checked) => handlePermissionChange(record.id, "can_manage_cleaning_work_orders", checked)}
                />
            )
        },
        {
            title: "其他&建议权限",
            key: "other_work_order_permission",
            width: 130,
            render: (_, record) => (
                <Switch
                    checked={record.permissions?.can_manage_other_work_orders === true}
                    onChange={(checked) => handlePermissionChange(record.id, "can_manage_other_work_orders", checked)}
                />
            )
        },
        { title: "操作", key: "action", render: (_, record) => (
            <Popconfirm title="确定删除该子账号吗？" onConfirm={() => handleDelete(record.id)}>
                <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
            </Popconfirm>
        )}
    ];

    return (
        <div>
            {loading && <PageLoadingOverlay caption="正在加载子账号，请稍候…" />}
            <Card 
                title="子账号管理" 
                extra={
                    <Space>
                        <Tag color="blue">最多可创建3个子账号</Tag>
                        <Tag color="green">已创建 {subAccounts.length} 个</Tag>
                    </Space>
                }
            >
                <div style={{ marginBottom: 16 }}>
                    <Button 
                        type="primary" 
                        icon={<PlusOutlined />} 
                        onClick={() => { form.resetFields(); setModalVisible(true); }}
                        disabled={subAccounts.length >= 3}
                    >
                        添加子账号
                    </Button>
                </div>
                <Table className="sub-account-permission-table" columns={columns} dataSource={subAccounts} rowKey="id" loading={loading} scroll={{ x: 1180 }} />
            </Card>

            <Modal title="添加子账号" open={modalVisible} onOk={handleCreate} onCancel={() => { if (!creating) setModalVisible(false); }} confirmLoading={creating} cancelButtonProps={{ disabled: creating }}>
                <Form form={form} layout="vertical">
                    <Form.Item name="username" label="用户名" rules={[{ required: true, message: "请输入用户名" }]}>
                        <Input placeholder="例如：staff1" />
                    </Form.Item>
                    <Form.Item name="password" label="密码" rules={[{ required: true, message: "请输入密码" }, { min: 6, message: "密码至少6位" }]}>
                        <Input.Password placeholder="至少6位" />
                    </Form.Item>
                    <Form.Item name="phone" label="手机号">
                        <Input placeholder="选填" />
                    </Form.Item>
                    <Form.Item name="can_manage_repair_work_orders" label="报修工单权限" valuePropName="checked" initialValue={false}>
                        <Switch checkedChildren="允许" unCheckedChildren="不允许" />
                    </Form.Item>
                    <Form.Item name="can_manage_cleaning_work_orders" label="保洁工单权限" valuePropName="checked" initialValue={false}>
                        <Switch checkedChildren="允许" unCheckedChildren="不允许" />
                    </Form.Item>
                    <Form.Item name="can_manage_other_work_orders" label="其他&建议工单权限" valuePropName="checked" initialValue={false}>
                        <Switch checkedChildren="允许" unCheckedChildren="不允许" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default SubAccountScreen;

